import React from 'react';

import { Button, Grid, Typography } from '@mui/material';

import { Nullable } from '@clearverify/types';

import {
  ActiveInactive,
  ConsumerDocument,
  EmployeeItemFragment,
  useConsumerUpdateMutation,
} from 'src/api';
import { useNotification } from 'src/hooks/app';
import { useModal } from 'src/hooks/shared';
import { SxProp } from 'src/types/mui';

export interface UnfreezeYourAccountModalContentProps {
  onCloseModal: () => void;
  switchClosability: ReturnType<typeof useModal>['switchClosability'];
  consumer: Nullable<EmployeeItemFragment>;
}

export const UnfreezeYourAccountModalContent: React.FC<UnfreezeYourAccountModalContentProps> = ({
  switchClosability,
  onCloseModal,
  consumer,
}) => {
  const { createApolloContext } = useNotification();

  const [consumerUpdate, { loading }] = useConsumerUpdateMutation({
    refetchQueries: [ConsumerDocument],
    context: createApolloContext({ successMessage: 'The changes have been saved' }),
  });

  const onUpdateRequestClick = React.useCallback(async () => {
    switchClosability(false);

    await consumerUpdate({
      variables: {
        where: { id: consumer?.id },
        data: {
          freeze: ActiveInactive.Inactive,
        },
      },
    });

    switchClosability(true);
    onCloseModal();
  }, [switchClosability, consumerUpdate, consumer?.id, onCloseModal]);

  return (
    <Grid container spacing={4}>
      <Grid size={12}>
        <Typography variant="h2" sx={titleStyles}>
          Are you sure?
        </Typography>
      </Grid>
      <Grid size={12}>
        <Typography variant="body1">Confirm you want to unfreeze your account</Typography>
      </Grid>
      <Grid container spacing={3} justifyContent="center" alignItems="center" size={12}>
        <Grid>
          <Button
            color="secondary"
            variant="outlined"
            onClick={onCloseModal}
            disabled={loading}
            loading={loading}
          >
            Cancel
          </Button>
        </Grid>
        <Grid>
          <Button
            color="primary"
            variant="contained"
            onClick={onUpdateRequestClick}
            disabled={loading}
            loading={loading}
          >
            Yes, unfreeze
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

const titleStyles: SxProp = {
  fontSize: '24px',
};
